import type { BaseListDocument, ColumnSpec } from './DocList.types';

export type FilterConflictResolver = (activeFilterIds: string[], newFilterId: string) => string[];

/**
 * Returns a function that, given current active filter IDs and a new filter ID
 * being added, returns the IDs of existing filters that must be removed to keep
 * the combination valid against `allowedFilterCombinations`.
 *
 * Picks the allowed combination that includes the new filter and preserves the
 * most existing filters. Filters not in that combination are returned for removal.
 */
function createFilterConflictResolver<TDoc extends BaseListDocument>(
    columns: ColumnSpec<TDoc>[],
    allowedFilterCombinations: string[][] | undefined,
): FilterConflictResolver {
    if (!allowedFilterCombinations || allowedFilterCombinations.length === 0) {
        return () => [];
    }

    const serverFilterableIds = new Set(
        columns
            .filter((c) => c.filterable && c.serverFilterField)
            .map((c) => c.id),
    );

    const allowedSets = allowedFilterCombinations.map((combo) => new Set(combo));

    return (activeFilterIds: string[], newFilterId: string): string[] => {
        if (!serverFilterableIds.has(newFilterId)) return [];

        const activeServerIds = activeFilterIds.filter((id) => serverFilterableIds.has(id));

        if (activeServerIds.includes(newFilterId)) return [];

        const candidateSet = new Set([...activeServerIds, newFilterId]);
        const isAlreadyValid = allowedSets.some(
            (allowed) =>
                allowed.size === candidateSet.size &&
                [...candidateSet].every((id) => allowed.has(id)),
        );
        if (isAlreadyValid) return [];

        const reachable = allowedSets.filter(
            (s) => s.has(newFilterId) && [...s].every((id) => candidateSet.has(id)),
        );
        if (reachable.length === 0) return activeServerIds;

        let bestCombo: Set<string> | null = null;
        let bestOverlap = -1;

        for (const combo of reachable) {
            const overlap = activeServerIds.filter((id) => combo.has(id)).length;
            if (overlap > bestOverlap || (overlap === bestOverlap && bestCombo && combo.size > bestCombo.size)) {
                bestOverlap = overlap;
                bestCombo = combo;
            }
        }

        if (!bestCombo) return activeServerIds;

        return activeServerIds.filter((id) => !bestCombo!.has(id));
    };
}

export default createFilterConflictResolver;
